import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Cropper, { type Area } from 'react-easy-crop';
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';

import Button from '@/components/Button';
import Modal, { type ModalProps, type ModalRef } from '@/components/Modal';
import { getCroppedImageFile } from '@/utils/image';
import { gray, radiusTokens } from '@/theme/themePrimitives';
import { type AvatarUploaderProps } from '@/components/AvatarUploader/index';
import { useTranslate } from '@/state/i18n';

const cropOutputMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];

type ImageCropModalProps = Omit<ModalProps, 'open' | 'title' | 'actions' | 'children'> &
  Required<Pick<AvatarUploaderProps, 'cropModalTitle' | 'cropShape'>> & {
    file: File | null;
    onConfirm: (file: File) => Promise<void> | void;
    cropAspect?: number;
    maxOutputSize?: number;
    quality?: number;
    outputMimeType?: string;
  };

const ImageCropModal = forwardRef<ModalRef, ImageCropModalProps>(
  (
    {
      file,
      onConfirm,
      cropModalTitle,
      cropShape,
      cropAspect = 1,
      maxOutputSize = 1024,
      quality = 0.84,
      outputMimeType,
      onDismiss,
      ...props
    },
    ref,
  ) => {
    const t = useTranslate('components.AvatarUploader.ImageCropModal');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const imageUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);

    useEffect(() => {
      return () => {
        if (imageUrl) URL.revokeObjectURL(imageUrl);
      };
    }, [imageUrl]);

    const resetState = useCallback(() => {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedAreaPixels(null);
      setError(null);
      setIsProcessing(false);
    }, []);

    const handleOpen = useCallback(() => {
      resetState();
    }, [resetState]);

    const handleDismiss = useCallback(() => {
      resetState();
      onDismiss?.();
    }, [onDismiss, resetState]);

    const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    }, []);

    const handleClose = useCallback(() => {
      if (isProcessing) return;
      if (ref && (ref as React.RefObject<ModalRef>).current) {
        (ref as React.RefObject<ModalRef>).current?.close();
      }
    }, [isProcessing, ref]);

    const handleConfirm = useCallback(async () => {
      if (!imageUrl || !croppedAreaPixels || !file) return;

      setIsProcessing(true);
      setError(null);

      try {
        const resolvedOutputMimeType =
          outputMimeType ?? (cropOutputMimeTypes.includes(file.type) ? file.type : undefined);
        const croppedFile = await getCroppedImageFile({
          imageSrc: imageUrl,
          pixelCrop: croppedAreaPixels,
          rotation,
          maxOutputSize,
          quality,
          outputMimeType: resolvedOutputMimeType,
        });
        await onConfirm(croppedFile);
        if (ref && (ref as React.RefObject<ModalRef>).current) {
          (ref as React.RefObject<ModalRef>).current?.close();
        }
      } catch {
        setError(t('processError'));
      } finally {
        setIsProcessing(false);
      }
    }, [
      t,
      imageUrl,
      croppedAreaPixels,
      file,
      rotation,
      maxOutputSize,
      quality,
      outputMimeType,
      onConfirm,
      ref,
    ]);

    return (
      <Modal
        ref={ref}
        {...props}
        title={cropModalTitle}
        maxWidth="sm"
        showClose
        closeDisabled={isProcessing}
        onOpen={handleOpen}
        onDismiss={handleDismiss}
        actions={
          <Stack direction="row" spacing={2} width="100%">
            <Button variant="outlined" fullWidth onClick={handleClose} disabled={isProcessing}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirm}
              loading={isProcessing}
              disabled={!croppedAreaPixels || isProcessing}
            >
              {t('update')}
            </Button>
          </Stack>
        }
      >
        <Stack gap={2}>
          <Box sx={sx.cropContainer}>
            {imageUrl ? (
              <Cropper
                image={imageUrl}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={cropAspect}
                cropShape={cropShape}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
              />
            ) : null}
          </Box>

          <Stack gap={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.05}
              aria-label={t('zoom')}
              onChange={(_, value) => setZoom(value as number)}
              disabled={isProcessing}
            />
          </Stack>

          <Stack gap={1}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t('rotation')}
            </Typography>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={1}
              aria-label={t('rotation')}
              onChange={(_, value) => setRotation(value as number)}
              disabled={isProcessing}
            />
          </Stack>

          {error ? (
            <Alert severity="error">
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          ) : null}
        </Stack>
      </Modal>
    );
  },
);

ImageCropModal.displayName = 'ImageCropModal';

export default ImageCropModal;

const sx = {
  cropContainer: {
    position: 'relative',
    width: '100%',
    height: 320,
    borderRadius: radiusTokens.xl,
    overflow: 'hidden',
    bgcolor: gray[900],
  },
};
